import { useCallback, useEffect, useRef, useState } from "react";

const normalizeMetering = (metering: number) => {
	const minDb = -60;
	const maxDb = -10;
	const clampedDb = Math.min(maxDb, Math.max(minDb, metering));
	return ((clampedDb - minDb) / (maxDb - minDb)) ** 2;
};

export const useWebMetering = (intervalInMs = 100) => {
	const [meterHistory, setMeterHistory] = useState<number[]>([]);
	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const meterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mediaStreamRef = useRef<MediaStream | null>(null);
	const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);

	const updateMeterHistory = useCallback((newLevel: number) => {
		setMeterHistory((prev) => [...prev, newLevel]);
	}, []);

	const startMetering = useCallback(async () => {
		if (!navigator?.mediaDevices?.getUserMedia) {
			return;
		}

		const stream = await navigator.mediaDevices.getUserMedia({
			audio: {
				echoCancellation: true,
				noiseSuppression: true,
				sampleRate: 128000,
			},
		});
		mediaStreamRef.current = stream;

		audioContextRef.current =
			new // @ts-expect-error does not detect webkitAudioContext
			(window.AudioContext || window.webkitAudioContext)();

		await audioContextRef.current.resume();
		const source = audioContextRef.current.createMediaStreamSource(stream);

		analyserRef.current = audioContextRef.current.createAnalyser();
		analyserRef.current.fftSize = 2048;
		source.connect(analyserRef.current);

		const buffer = new ArrayBuffer(analyserRef.current.fftSize);
		dataArrayRef.current = new Uint8Array(buffer);

		meterIntervalRef.current = setInterval(() => {
			const analyser = analyserRef.current;
			const dataArray = dataArrayRef.current;

			if (!(analyser && dataArray)) {
				return;
			}

			analyser.getByteTimeDomainData(dataArray);

			let sum = 0;
			for (const value of dataArray) {
				const v = (value - 128) / 128;
				sum += v * v;
			}
			const rms = Math.sqrt(sum / dataArray.length);
			const newLevel = 20 * Math.log10(rms || 0.0001);

			updateMeterHistory(normalizeMetering(newLevel));
		}, intervalInMs);
	}, [updateMeterHistory, intervalInMs]);

	const stopMetering = useCallback(async () => {
		if (meterIntervalRef.current) {
			clearInterval(meterIntervalRef.current);
			meterIntervalRef.current = null;
		}
		if (mediaStreamRef.current) {
			for (const track of mediaStreamRef.current.getTracks()) {
				track.stop();
				mediaStreamRef.current.removeTrack(track);
			}
			mediaStreamRef.current = null;
		}
		if (audioContextRef.current) {
			await audioContextRef.current.close();
			audioContextRef.current = null;
		}
		analyserRef.current = null;
		dataArrayRef.current = null;
	}, []);

	const clearMeterHistory = useCallback(() => {
		setMeterHistory([]);
	}, []);

	useEffect(() => {
		return () => {
			stopMetering();
		};
	}, [stopMetering]);

	return {
		meterHistory,
		startMetering,
		stopMetering,
		clearMeterHistory,
	};
};
